/**
 * One page per futures book across seasons. Each season of a book lists under its own
 * ticker (`kxsb-26`, then `kxsb-27`), so each season's URL used to earn a year of rankings
 * and then die with the book. Like the NFL matchup pages
 * (`markets/[...slug]/nflMatchupCanonical.ts`), one evergreen URL owns the query instead:
 * the series URL (`/markets/kxsb/super-bowl`) renders the current season in place, every
 * season's URL names it as its canonical, and the sitemap lists it rather than the seasons.
 * The season pages stay live and tradable; only the ranking signal consolidates.
 *
 * Plain ESM with no imports: the event page and the deploy-time sitemap generator
 * (`sitemapper.js`, plain `node`) apply the same rule.
 */

/**
 * The futures and awards whose seasons roll over — every curated one in
 * `react_pages/Event/seo/marketHeadKeywords.ts` marked `rollover` (kept equal by a test), and the
 * guide-page books below — each with its series page's description: the slug of the series' title on
 * 22 Oct 2026, so a link built without the title still lands on the series page in one hop. Games
 * never roll over: a settled game page still earns clicks.
 *
 * The registry's retired NFL award aliases (`KXNFLOPOY`, `KXNFLCOACH`…) are left out: on 22 Oct 2026
 * they hold no events at all — every season lists under the live `*POTY`/`*ROTY`/`KXNFLCOTY` tickers —
 * so there is no alias URL to roll over.
 */
const CURATED_ROLLOVER_SERIES = [
    ['KXSB', 'super-bowl'],
    ['KXNFLAFCCHAMP', 'american-football-conference-champion'],
    ['KXNFLNFCCHAMP', 'national-football-conference-champion'],
    ['KXNFLMVP', 'ap-pro-football-regular-season-mvp'],
    ['KXNFLOPOTY', 'ap-pro-football-offensive-player-of-the-year'],
    ['KXNFLDPOTY', 'ap-pro-football-defensive-player-of-the-year'],
    ['KXNFLOROTY', 'ap-pro-football-offensive-rookie-of-the-year'],
    ['KXNFLDROTY', 'ap-pro-football-defensive-rookie-of-the-year'],
    ['KXNFLCPOTY', 'ap-pro-football-comeback-player-of-the-year'],
    ['KXNFLCOTY', 'ap-pro-football-coach-of-the-year'],
    ['KXHEISMAN', 'heisman-trophy-winner'],
    ['KXNCAAF', 'ncaaf-championship'],
    ['KXMLB', 'world-series'],
    ['KXMLBALMVP', 'pro-baseball-american-league-mvp'],
    ['KXMLBNLMVP', 'pro-baseball-national-league-mvp'],
    ['KXMLBALCY', 'pro-baseball-american-league-cy-young'],
    ['KXMLBNLCY', 'pro-baseball-national-league-cy-young'],
    ['KXMLBALROTY', 'pro-baseball-american-league-rookie-of-the-year'],
    ['KXMLBNLROTY', 'pro-baseball-national-league-rookie-of-the-year'],
    ['KXNBA', 'pro-basketball-champion'],
    ['KXNBAMVP', 'nba-mvp'],
    ['KXNBAROY', 'nba-rookie-of-the-year'],
    ['KXNBADPOY', 'nba-defensive-player-of-the-year'],
    ['KXNBACOY', 'nba-coach-of-the-year'],
    ['KXNHL', 'stanley-cup'],
    ['KXNHLHART', 'nhl-hart-memorial-trophy'],
    ['KXNHLVEZINA', 'nhl-vezina-trophy'],
    ['KXNHLNORRIS', 'nhl-james-norris-memorial-trophy'],
    ['KXNHLCALDER', 'nhl-calder-memorial-trophy'],
];

/**
 * Books with a guide page but no curated head keyword (their names derive from the event):
 * each lists one `SERIES-YY` event per season, so each season's URL earned its own rankings
 * and died with the book. Slugs from the series' titles on 2 Nov 2026. `KXNBAPLAYIN` is not
 * here: it lists a book per conference (`-27EAST`, `-27WEST`), not one per season.
 */
const GUIDE_BOOK_DESCRIPTIONS = [
    ['KXNHLROSS', 'nhl-art-ross-trophy'],
    ['KXNHLRICHARD', 'nhl-maurice-rocket-richard-trophy'],
    ['KXNHLADAMS', 'nhl-jack-adams-award'],
    ['KXNHLPLAYOFF', 'playoff-qualifier'],
    ['KXNHLPRES', 'nhl-presidents-trophy-winner'],
    ['KXNBAPLAYOFF', 'pro-basketball-playoff-qualifier'],
    ['KXMLBPLAYOFFS', 'pro-baseball-playoff-qualifiers'],
    // Slugs from the series' titles on 3 Nov 2026.
    ['KXNFLPLAYOFF', 'pro-football-playoff-qualifiers'],
    ['KXNCAAFPLAYOFF', 'college-football-playoff-qualifiers'],
];

/** The guide-page books, which the head-keyword test leaves out. */
export const GUIDE_BOOK_ROLLOVER_SERIES = new Set(GUIDE_BOOK_DESCRIPTIONS.map(([series]) => series));

export const ROLLOVER_SERIES = new Map([...CURATED_ROLLOVER_SERIES, ...GUIDE_BOOK_DESCRIPTIONS]);

/** A season's event ticker: the series and a two-digit year, nothing after it (`KXSB-27`). */
const SEASON_TICKER = /^([A-Z0-9]+)-(\d{2})$/;

const seasonOf = (series, ticker) => {
    const match = SEASON_TICKER.exec((ticker ?? '').toUpperCase());
    return match && match[1] === series ? Number(match[2]) : undefined;
};

/** Whether an event is one season of a rollover book, which its series URL stands for. */
export function isSeasonTicker(seriesTicker, eventTicker) {
    const series = (seriesTicker ?? '').toUpperCase();
    return ROLLOVER_SERIES.has(series) && seasonOf(series, eventTicker) !== undefined;
}

/**
 * The series page a link to this event belongs on (`/markets/kxsb/super-bowl`), or undefined for
 * anything that isn't a season of a rollover book. Every internal link, nav entry and sitemap
 * entry for a season points here, never at the season's own URL: links are as much what makes
 * the series page the one that ranks as its canonical is.
 *
 * @param {string | undefined} seriesTicker
 * @param {string | undefined} eventTicker
 * @param {string} [description] The series title's slug when the caller has it; the registry's otherwise.
 * @returns {string | undefined}
 */
export function seasonPagePath(seriesTicker, eventTicker, description) {
    if (!isSeasonTicker(seriesTicker, eventTicker)) return undefined;
    const series = (seriesTicker ?? '').toUpperCase();
    return `/markets/${series.toLowerCase()}/${description || ROLLOVER_SERIES.get(series)}`;
}

/**
 * An event page path (`/markets/kxsb/super-bowl/kxsb-27`, query kept) moved onto its series page
 * when it names a season of a rollover book, for paths built elsewhere (the backend's `event_path`).
 * The description is the registry's, not the path's middle segment, which a backend has sent as
 * `events` before.
 *
 * @param {string} path
 * @returns {string}
 */
export function toSeasonPagePath(path) {
    const [pathname, query] = path.split('?');
    const segments = pathname.split('/').filter(Boolean);
    if (segments[0] !== 'markets' || segments.length < 3) return path;
    const seasonPage = seasonPagePath(segments[1], segments[segments.length - 1]);
    return seasonPage ? `${seasonPage}${query ? `?${query}` : ''}` : path;
}

/**
 * Whether a markets URL's segments (after `/markets/`) are a rollover book's series page
 * (`kxsb/super-bowl`), which renders the current season in place. A series-prefixed second
 * segment is an event ticker and `draft` an admin preview, so neither is; the description itself
 * is checked by the page, which knows the series' title.
 *
 * @param {readonly string[]} segments
 */
export function isSeasonPagePath(segments) {
    if (segments.length !== 2) return false;
    const [series, description] = segments.map((segment) => segment.toLowerCase());
    return ROLLOVER_SERIES.has(series.toUpperCase()) && description !== 'draft' && !description.startsWith(series);
}

/**
 * The season a rollover book's series URL renders: the earliest one still trading, so a live
 * World Series stays on screen after next season's book lists; otherwise, between seasons, the
 * latest one.
 *
 * @param {string | undefined} seriesTicker
 * @param {{ open: readonly string[], all: readonly string[] }} events
 *   The series' event tickers: those with a market still trading, and every one listed.
 * @returns {string | undefined} The season's ticker, upper-cased.
 */
export function currentSeason(seriesTicker, { open, all }) {
    const series = (seriesTicker ?? '').toUpperCase();
    if (!ROLLOVER_SERIES.has(series)) return undefined;
    const seasons = (tickers) =>
        tickers
            .map((t) => ({ ticker: t.toUpperCase(), season: seasonOf(series, t) }))
            .filter((entry) => entry.season !== undefined)
            .sort((a, b) => a.season - b.season);
    return seasons(open)[0]?.ticker ?? seasons(all).at(-1)?.ticker;
}

/**
 * The season number of an event that is a rollover series' season and has ended, or undefined
 * for anything else: a live season, a game, another series.
 *
 * @param {string | undefined} seriesTicker
 * @param {string | undefined} eventTicker
 * @param {{ open: readonly string[], closed?: readonly string[], settled: readonly string[] }} seasons
 *   The series' event tickers by status. "Settled" means any market has settled — an
 *   open book that has graded its eliminated teams is both, and so is a book whose trading
 *   has stopped while its final result is pending (closed) — so only a season that is
 *   settled and neither open nor closed has ended.
 * @returns {number | undefined}
 */
export function endedSeason(seriesTicker, eventTicker, { open, closed = [], settled }) {
    const series = (seriesTicker ?? '').toUpperCase();
    if (!ROLLOVER_SERIES.has(series)) return undefined;
    const ticker = (eventTicker ?? '').toUpperCase();
    const season = seasonOf(series, ticker);
    if (season === undefined) return undefined;
    const has = (tickers) => tickers.some((t) => t.toUpperCase() === ticker);
    if (has(open) || has(closed) || !has(settled)) return undefined;
    return season;
}

/**
 * The season an event's page should canonicalize to, or undefined to keep its own.
 *
 * @param {string | undefined} seriesTicker
 * @param {string | undefined} eventTicker
 * @param {{ open: readonly string[], closed?: readonly string[], settled: readonly string[] }} seasons
 *   As for `endedSeason`.
 * @returns {string | undefined} The earliest open season after this one, once this one has ended.
 */
export function rolloverTarget(seriesTicker, eventTicker, seasons) {
    const season = endedSeason(seriesTicker, eventTicker, seasons);
    if (season === undefined) return undefined;
    const series = (seriesTicker ?? '').toUpperCase();
    const next = seasons.open
        .map((t) => ({ ticker: t.toUpperCase(), season: seasonOf(series, t) }))
        .filter((entry) => entry.season !== undefined && entry.season > season)
        .sort((a, b) => a.season - b.season)[0];
    return next?.ticker;
}

/**
 * The same statuses read from `/v1/events` payloads with their markets, for the sitemap
 * generator: open when any market is active, closed when one has stopped trading without a
 * result, settled when any has a result.
 *
 * @param {readonly { ticker: string, markets?: readonly { status?: string, result?: string }[] }[]} events
 */
export function seasonStatusesFromEvents(events) {
    const open = [];
    const closed = [];
    const settled = [];
    for (const event of events) {
        const markets = event.markets ?? [];
        if (markets.some((market) => market.status === 'active')) open.push(event.ticker);
        // Trading stopped, result pending: a contract with no result that is no longer active.
        if (markets.some((market) => !market.result && market.status !== 'active')) closed.push(event.ticker);
        if (markets.some((market) => !!market.result)) settled.push(event.ticker);
    }
    return { open, closed, settled };
}
